import { Stack } from '@mui/material';
import { debounce } from 'lodash';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

const stuckShadow = '0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.30)';

interface StickyElementProps {
  children: ReactNode,
}

const StickyElement = ({
  children,
}: StickyElementProps) => {
  const [isSticky, setSticky] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection = debounce(([entry]: IntersectionObserverEntry[]) => {
    setSticky(!entry.isIntersecting);
  }, 100);

  useEffect(() => {
    const sentinelNode = sentinelRef.current;
    const observer = new IntersectionObserver(
      handleIntersection,
      {
        root: null, // Observe relative to the viewport
        rootMargin: '-1px 0px 0px 0px',
        threshold: 0, // Trigger as soon as the sentinel leaves the viewport
      },
    );

    if (sentinelNode) {
      observer.observe(sentinelNode);
    }

    return () => {
      if (sentinelNode) {
        observer.unobserve(sentinelNode);
      }
    };
  }, [sentinelRef, handleIntersection]);

  // Sticky positioning keeps the element inside its own column; a fixed element would span
  // the whole window once the dashboard column is centered. The sentinel only drives the shadow.
  return (
    <>
      <Stack ref={sentinelRef} style={{ height: '1px', marginTop: '0px' }} />
      <Stack
        data-testid="sticky-element"
        sx={{
          ...(isSticky ? { boxShadow: stuckShadow } : {}),
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
      >
        {children}
      </Stack>
    </>
  );
};

export default StickyElement;
